import { FileCopy as FileCopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { MouseEventHandler, useState } from 'react';
import { Box, Button, Tooltip, Typography, alpha } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

type Props = {
  textToCopy: string;
  label?: string;
  variant?: 'icon' | 'button' | 'inline';
  size?: 'small' | 'medium' | 'large';
  showPreview?: boolean;
};

type VariantProps = {
  theme: Theme;
  isTextCopied: boolean;
  disabled: boolean;
  iconSize: number;
  tooltipText: string;
  label?: string;
  size: NonNullable<Props['size']>;
  onCopy: MouseEventHandler<HTMLElement>;
};

const getIconSize = (size: NonNullable<Props['size']>) =>
  size === 'small' ? 18 : size === 'large' ? 24 : 20;

const getTooltipText = (
  isTextCopied: boolean,
  disabled: boolean,
  showPreview: boolean,
  textToCopy: string,
) =>
  isTextCopied
    ? 'Copied!'
    : disabled
      ? 'Nothing to copy'
      : showPreview && textToCopy
        ? textToCopy
        : 'Click to copy';

const CopyButtonVariant: React.FC<VariantProps> = ({
  theme,
  isTextCopied,
  disabled,
  iconSize,
  tooltipText,
  label,
  size,
  onCopy,
}) => (
  <Tooltip title={tooltipText} arrow>
    <span>
      <Button
        variant="outlined"
        size={size}
        disabled={disabled}
        onClick={onCopy}
        startIcon={
          isTextCopied ? (
            <CheckIcon sx={{ fontSize: iconSize }} />
          ) : (
            <FileCopyIcon sx={{ fontSize: iconSize }} />
          )
        }
        sx={{
          color: isTextCopied ? theme.palette.success.main : undefined,
          borderColor: isTextCopied ? theme.palette.success.main : undefined,
          '&:hover': {
            borderColor: isTextCopied ? theme.palette.success.main : undefined,
            backgroundColor: isTextCopied ? alpha(theme.palette.success.main, 0.08) : undefined,
          },
        }}
      >
        {label || (isTextCopied ? 'Copied' : 'Copy')}
      </Button>
    </span>
  </Tooltip>
);

const CopyInlineVariant: React.FC<VariantProps> = ({
  theme,
  isTextCopied,
  disabled,
  iconSize,
  tooltipText,
  label,
  onCopy,
}) => (
  <Box
    onClick={onCopy}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      padding: theme.spacing(0.5, 1),
      borderRadius: 1,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: !disabled ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
      },
    }}
  >
    {label && (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: isTextCopied ? theme.palette.success.main : theme.palette.text.secondary,
        }}
      >
        {label}
      </Typography>
    )}
    <Tooltip title={tooltipText} arrow>
      {isTextCopied ? (
        <CheckIcon
          sx={{
            fontSize: iconSize,
            color: theme.palette.success.main,
          }}
        />
      ) : (
        <FileCopyIcon
          sx={{
            fontSize: iconSize,
            color: theme.palette.action.active,
          }}
        />
      )}
    </Tooltip>
  </Box>
);

const CopyIconVariant: React.FC<VariantProps> = ({
  theme,
  isTextCopied,
  disabled,
  iconSize,
  tooltipText,
  onCopy,
}) => (
  <Tooltip title={tooltipText} arrow>
    <Box
      component="span"
      onClick={!disabled ? onCopy : undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {isTextCopied ? (
        <CheckIcon
          sx={{
            fontSize: iconSize,
            color: theme.palette.success.main,
            cursor: 'default',
          }}
        />
      ) : (
        <FileCopyIcon
          color={disabled ? 'disabled' : 'primary'}
          sx={{
            fontSize: iconSize,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          className="copy-icon"
        />
      )}
    </Box>
  </Tooltip>
);

const Copy: React.FC<Props> = ({
  textToCopy,
  label,
  variant = 'icon',
  size = 'medium',
  showPreview = false,
}) => {
  const theme = useTheme();
  const [isTextCopied, setIsTextCopied] = useState(false);

  const copyToClipboard: MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation();

    if (textToCopy) {
      void navigator.clipboard.writeText(textToCopy);
      setIsTextCopied(true);

      // Reset state after 3 seconds
      setTimeout(() => {
        setIsTextCopied(false);
      }, 3000);
    }
  };

  const iconSize = getIconSize(size);
  const disabled = !textToCopy;
  const tooltipText = getTooltipText(isTextCopied, disabled, showPreview, textToCopy);

  const variantProps: VariantProps = {
    theme,
    isTextCopied,
    disabled,
    iconSize,
    tooltipText,
    label,
    size,
    onCopy: copyToClipboard,
  };

  if (variant === 'button') {
    return <CopyButtonVariant {...variantProps} />;
  }

  if (variant === 'inline') {
    return <CopyInlineVariant {...variantProps} />;
  }

  // Default: icon variant
  return <CopyIconVariant {...variantProps} />;
};

export default Copy;
